import { useCallback, useEffect, useState } from 'react';

/**
 * Load a list, keep the previous rows visible while it reloads, and surface failures.
 *
 * Keeping the old rows on screen during a refresh is the small thing that separates a product
 * that feels considered from one that blinks to empty every time you change something. It is
 * also what makes an optimistic action feel instant rather than flickering.
 */
export function useResource<T>(load: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(true);

  const run = useCallback(async () => {
    setBusy(true); setError(null);
    try { setData(await load()); } catch (e) { setError(e); } finally { setBusy(false); }
    // `load` is expected to be a useCallback in the caller; see FigProvider's stable client.
  }, [load]);

  useEffect(() => { void run(); }, [run]);
  return { data, error, busy, reload: run, setData };
}
